// JWT helper + decode untuk legacy claims yang di-issue api-server.
// Token disimpan/dibaca via Zustand `authStore` (lihat src/store/auth-store.ts) —
// helper di sini cuma utility murni (no state).

export interface LegacyClaims {
  authorized?: boolean;
  user?: string;    // login name (mis. "admin")
  id: number;       // user.id
  level_id: number; // 1=admin, 2=operator, 3=viewer, 4=reseller
  vehicles?: string; // CSV legacy (jarang dipakai di webgps, abaikan saja)
  exp: number;      // unix expiry
  iat?: number;
}

/**
 * decodeClaims: decode + NORMALISASI claims JWT.
 *
 * api-server (Go) menerbitkan field `uid` & `lvl` (lihat auth.go), sedangkan
 * monolith/web lama memakai `id` & `level_id`. Fungsi ini menormalkan keduanya
 * ke bentuk LegacyClaims (id/level_id) supaya kode UI (canSwitchUser,
 * effectiveUserId, RoleGuard) konsisten apa pun penerbit token-nya.
 */
export function decodeClaims(token: string): LegacyClaims | null {
  const raw = parseJwt<Record<string, unknown>>(token);
  if (!raw) return null;
  const num = (v: unknown) => (typeof v === 'number' ? v : Number(v) || 0);
  return {
    id: num(raw.uid ?? raw.id),
    level_id: num(raw.lvl ?? raw.level_id),
    user: typeof raw.user === 'string' ? raw.user : undefined,
    exp: num(raw.exp),
    iat: raw.iat != null ? num(raw.iat) : undefined,
  };
}

/**
 * parseJwt: decode payload bagian (claims) dari JWT tanpa verify signature.
 * Verify signature itu tugas server — frontend cuma butuh claims untuk UI.
 *
 * Catatan: pakai atob() — di Node test perlu polyfill, tapi di browser native.
 */
export function parseJwt<T = LegacyClaims>(token: string): T | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    return JSON.parse(json) as T;
  } catch {
    return null;
  }
}

/**
 * isExpired: cek apakah JWT sudah expire (claim `exp` lewat sekarang).
 * Kalau parse gagal atau no exp → anggap expired (safer default).
 */
export function isExpired(token: string): boolean {
  const claims = parseJwt(token);
  if (!claims || !('exp' in claims) || typeof claims.exp !== 'number') return true;
  return claims.exp * 1000 < Date.now();
}
